import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useSearchParams } from 'react-router-dom';
import { hasPublicPrice } from '../utils/propertyPrice';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export interface PropertyFilters {
  zones: FilterOption[];
  propertyTypes: FilterOption[];
  priceRanges: FilterOption[];
  totalRooms: FilterOption[];
  statuses: FilterOption[];
}

export const usePropertyFilters = () => {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<PropertyFilters>({
    zones: [],
    propertyTypes: [],
    priceRanges: [],
    totalRooms: [],
    statuses: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchFilters = async () => {
    try {
      // Get property statuses
      const { data: statusesData, error: statusesError } = await supabase
        .from('property_statuses')
        .select('*')
        .order('name');

      if (statusesError) throw statusesError;

      const { data: properties, error: propertiesError } = await supabase
        .from('properties')
        .select(`
          *,
          zones (
            id,
            name
          ),
          property_types (
            id,
            name
          ),
          property_statuses (
            id,
            name
          )
        `);

      if (propertiesError) throw propertiesError;

      // Process zones
      const zonesMap = new Map();
      properties?.forEach(property => {
        if (property.zones) {
          const zone = property.zones;
          if (!zonesMap.has(zone.id)) {
            zonesMap.set(zone.id, {
              value: zone.id,
              label: zone.name,
              count: 1
            });
          } else {
            const current = zonesMap.get(zone.id);
            zonesMap.set(zone.id, { ...current, count: current.count + 1 });
          }
        }
      });

      // Process property types
      const typesMap = new Map();
      properties?.forEach(property => {
        if (property.property_types) {
          const type = property.property_types;
          if (!typesMap.has(type.id)) {
            typesMap.set(type.id, {
              value: type.id,
              label: type.name,
              count: 1
            });
          } else {
            const current = typesMap.get(type.id);
            typesMap.set(type.id, { ...current, count: current.count + 1 });
          }
        }
      });

      // Process total rooms
      const roomsMap = new Map();
      properties?.forEach(property => {
        const rooms = property.total_rooms || property.bedrooms;
        if (rooms) {
          let rangeKey;
          let rangeLabel;
          
          if (rooms <= 2) {
            rangeKey = '1-2';
            rangeLabel = '1-2 vani';
          } else if (rooms <= 3) {
            rangeKey = '2-3';
            rangeLabel = '2-3 vani';
          } else if (rooms <= 4) {
            rangeKey = '3-4';
            rangeLabel = '3-4 vani';
          } else if (rooms <= 5) {
            rangeKey = '4-5';
            rangeLabel = '4-5 vani';
          } else {
            rangeKey = '5-999';
            rangeLabel = '5+ vani';
          }

          if (!roomsMap.has(rangeKey)) {
            roomsMap.set(rangeKey, {
              value: rangeKey,
              label: rangeLabel,
              count: 1
            });
          } else {
            const current = roomsMap.get(rangeKey);
            roomsMap.set(rangeKey, { ...current, count: current.count + 1 });
          }
        }
      });

      // Process price ranges for both sale and rent
      const priceRangesMap = new Map();
      
      // Helper function to add price range
      const addPriceRange = (type: string, min: number, max: number | null, label: string) => {
        const key = `${type}-${min}-${max || 0}`;
        if (!priceRangesMap.has(key)) {
          priceRangesMap.set(key, {
            value: key,
            label,
            count: 0
          });
        }
      };

      // Add all possible ranges
      // For sale
      addPriceRange('vendita', 0, 100000, 'Fino a €100.000');
      addPriceRange('vendita', 100000, 200000, '€100.000 - €200.000');
      addPriceRange('vendita', 200000, 300000, '€200.000 - €300.000');
      addPriceRange('vendita', 300000, 400000, '€300.000 - €400.000');
      addPriceRange('vendita', 400000, 500000, '€400.000 - €500.000');
      addPriceRange('vendita', 500000, null, 'Oltre €500.000');

      // For rent
      addPriceRange('affitto', 0, 500, 'Fino a €500/mese');
      addPriceRange('affitto', 500, 800, '€500 - €800/mese');
      addPriceRange('affitto', 800, 1000, '€800 - €1.000/mese');
      addPriceRange('affitto', 1000, 1500, '€1.000 - €1.500/mese');
      addPriceRange('affitto', 1500, 2000, '€1.500 - €2.000/mese');
      addPriceRange('affitto', 2000, null, 'Oltre €2.000/mese');

      // Count properties in each range
      properties?.forEach(property => {
        // I prezzi riservati compaiono nell'elenco senza filtro di prezzo,
        // ma non contribuiscono a fasce e conteggi numerici.
        if (!hasPublicPrice(property)) return;
        const price = property.price;
        const type = property.type;
        let rangeKey = '';

        if (type === 'vendita') {
          if (price <= 100000) rangeKey = 'vendita-0-100000';
          else if (price <= 200000) rangeKey = 'vendita-100000-200000';
          else if (price <= 300000) rangeKey = 'vendita-200000-300000';
          else if (price <= 400000) rangeKey = 'vendita-300000-400000';
          else if (price <= 500000) rangeKey = 'vendita-400000-500000';
          else rangeKey = 'vendita-500000-0';
        } else {
          if (price <= 500) rangeKey = 'affitto-0-500';
          else if (price <= 800) rangeKey = 'affitto-500-800';
          else if (price <= 1000) rangeKey = 'affitto-800-1000';
          else if (price <= 1500) rangeKey = 'affitto-1000-1500';
          else if (price <= 2000) rangeKey = 'affitto-1500-2000';
          else rangeKey = 'affitto-2000-0';
        }

        if (priceRangesMap.has(rangeKey)) {
          const current = priceRangesMap.get(rangeKey);
          priceRangesMap.set(rangeKey, { ...current, count: current.count + 1 });
        }
      });

      // Process statuses
      const statusesMap = new Map();
      statusesData?.forEach(status => {
        const count = properties?.filter(p => p.status_id === status.id).length || 0;
        statusesMap.set(status.id, {
          value: status.id,
          label: status.name === 'published' ? 'Pubblicato' :
                 status.name === 'draft' ? 'Bozza' :
                 status.name === 'archived' ? 'Archiviato' : status.name,
          count
        });
      });

      setFilters({
        zones: Array.from(zonesMap.values()),
        propertyTypes: Array.from(typesMap.values()),
        totalRooms: Array.from(roomsMap.values()),
        priceRanges: Array.from(priceRangesMap.values()),
        statuses: Array.from(statusesMap.values())
      });

    } catch (err: any) {
      console.error('Error fetching filters:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setLastUpdate(Date.now());
    }
  };

  // Funzione per forzare un aggiornamento dei filtri
  const refreshFilters = () => {
    setLastUpdate(Date.now());
    fetchFilters();
  };

  return { filters, loading, error, refreshFilters, lastUpdate };
};